import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { UploadCloud, FileSpreadsheet } from "lucide-react";

const COLUMNS = [
  ["patient_name", "Required. Patient full name."],
  ["accession_number", "Optional. Keeps your legacy number, otherwise one is generated."],
  ["patient_mrn / mrn", "Optional medical record number."],
  ["patient_dob / dob", "Optional date of birth."],
  ["ordering_physician / physician", "Optional."],
  ["specimen_type / specimen", "Optional."],
  ["priority", "Routine or STAT."],
  ["status", "Received, Grossed, Embedded, Cut, Stained or Sign-Out."],
  ["received_at", "ISO date, e.g. 2026-05-04."],
  ["gross_description, comments, diagnosis", "Optional narrative text."],
];

export default function ImportData() {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/import/cases", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported ${data.created} case(s)`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const csv = "patient_name,patient_mrn,patient_dob,ordering_physician,specimen_type,specimen_site,priority,status,received_at,gross_description\nJane Doe,MRN-1001,1968-04-11,Dr. Reyes,Skin,Left shoulder,Routine,Received,2026-06-01,Tan-pink skin ellipse.\n";
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "pathtrax_case_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="import-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Import Existing Data</h1>
        <p className="mt-1 text-sm text-slate-600">
          Export your Access or Excel case table to CSV, then upload it here. Every imported case gets an audit entry.
        </p>
      </div>

      <div className="border border-slate-200 p-5" data-testid="import-uploader">
        <input
          type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid="import-file-input" className="block w-full text-sm"
        />
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={upload} disabled={!file || busy} data-testid="import-upload-button" className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
            <UploadCloud size={15} className="mr-2" />{busy ? "Importing…" : "Import cases"}
          </Button>
          <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-button" className="h-11">
            <FileSpreadsheet size={15} className="mr-2" /> Download CSV template
          </Button>
        </div>
        {result && (
          <div className="mt-4 border-l-2 border-[#134e4a] bg-slate-50 p-3 text-sm" data-testid="import-result">
            <div className="font-semibold text-slate-900">Imported {result.created} case(s).</div>
            {result.errors?.length > 0 && (
              <ul className="mt-2 list-disc pl-5 text-red-800">
                {result.errors.slice(0, 20).map((err) => <li key={err}>{err}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="border border-slate-200" data-testid="import-columns">
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
          Recognised columns
        </div>
        <table className="w-full text-sm">
          <tbody>
            {COLUMNS.map(([col, note]) => (
              <tr key={col} className="border-b border-slate-100">
                <td className="w-1/3 px-4 py-2 font-mono text-xs text-[#134e4a]">{col}</td>
                <td className="px-4 py-2 text-slate-700">{note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
